import { useLayoutEffect, useState } from 'react';

export interface UseSmoothedTimeProps {
    currentTime: number;
    isPaused: boolean;
    playbackRate: number;
    duration: number;
}

/**
 * Interpolates `currentTime` between the ~4Hz `timeupdate` ticks via
 * requestAnimationFrame, so a bound scrubber moves smoothly during playback
 * instead of stepping every ~250ms. Re-anchors to the authoritative time on
 * every render (tick, seek, pause/resume, track change), so a deliberate
 * position change lands on the next frame instead of easing there.
 */
export function useSmoothedTime({
    currentTime,
    isPaused,
    playbackRate,
    duration,
}: UseSmoothedTimeProps): number {
    const [smoothedTime, setSmoothedTime] = useState(currentTime);

    useLayoutEffect(() => {
        setSmoothedTime(currentTime);

        if (isPaused) return;

        const anchorTime = currentTime;
        const anchorTimestamp = performance.now();
        let frameId: number;

        const tick = () => {
            const elapsedSeconds = (performance.now() - anchorTimestamp) / 1000;
            const nextTime = anchorTime + elapsedSeconds * playbackRate;
            setSmoothedTime(
                duration > 0 ? Math.min(nextTime, duration) : nextTime
            );
            frameId = requestAnimationFrame(tick);
        };
        frameId = requestAnimationFrame(tick);

        return () => cancelAnimationFrame(frameId);
    }, [currentTime, isPaused, playbackRate, duration]);

    return smoothedTime;
}
